import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { isStaticAssetUrl } from 'app/core/utils/static-asset-url';
import { neraApiErrorInterceptor } from 'nera-common/angular';
import { catchError, throwError } from 'rxjs';

/**
 * What a failed API call rejects with once `neraApiErrorInterceptor` has
 * toasted it: the same `{ status, message, traceId, originalError }` fields
 * callers already read, on a real `Error`.
 *
 * nera-common (<= 0.26) rethrows a plain object. Sentry titles a plain object
 * "Object captured as exception" and drops its stack (PFL-FE-WEB-S, kb#2220);
 * an `Error` subclass keeps a readable title and a stack.
 */
export class NeraApiError extends Error {
    readonly status: number;
    readonly traceId: string | undefined;
    readonly originalError: unknown;

    constructor(
        status: number,
        message: string,
        traceId: string | undefined,
        originalError: unknown
    ) {
        super(message);
        this.name = 'NeraApiError';
        this.status = status;
        this.traceId = traceId;
        this.originalError = originalError;
    }
}

/** The plain object nera-common's interceptor rethrows for a non-401 failure. */
interface NormalizedApiFailure {
    status: number;
    message?: unknown;
    traceId?: unknown;
    originalError: unknown;
}

function isNormalizedApiFailure(error: unknown): error is NormalizedApiFailure {
    return (
        typeof error === 'object' &&
        error !== null &&
        !(error instanceof Error) &&
        !(error instanceof HttpErrorResponse) &&
        'originalError' in error &&
        typeof (error as { status?: unknown }).status === 'number'
    );
}

/**
 * Wraps nera-common's normalized rejection in a `NeraApiError`. Anything else
 * (a 401 `HttpErrorResponse` passed through for the auth interceptor, an
 * `Error` already) is returned unchanged.
 */
export function toNeraApiError(error: unknown): unknown {
    if (!isNormalizedApiFailure(error)) {
        return error;
    }
    return new NeraApiError(
        error.status,
        typeof error.message === 'string' ? error.message : '',
        typeof error.traceId === 'string' ? error.traceId : undefined,
        error.originalError
    );
}

/**
 * The app's API error interceptor: `neraApiErrorInterceptor` (translated toast,
 * page-level 403 redirect, logging) with two fixes on top (kb#2220):
 *
 * - Static assets (i18n bundles, icon sprites, images, fonts) pass through
 *   untouched — no "cannot reach server" toast, and the caller gets the
 *   original `HttpErrorResponse`. A failed `icons/*.svg` fetch used to toast
 *   and hand `MatIconRegistry` the API message as its error
 *   (PFL-FE-WEB-K/-P/-R).
 * - The normalized rejection is rethrown as a `NeraApiError`, not a plain
 *   object.
 *
 * Takes `neraApiErrorInterceptor`'s slot in `authInterceptors`; the ordering
 * notes there still apply.
 */
export const apiErrorInterceptor: HttpInterceptorFn = (req, next) => {
    if (isStaticAssetUrl(req.url)) {
        return next(req);
    }
    return neraApiErrorInterceptor(req, next).pipe(
        catchError((error: unknown) => throwError(() => toNeraApiError(error)))
    );
};
